import React, { useEffect, useState } from "react";
import { useDeal } from "../lib/store";
import { shareApi, firmApi } from "../lib/api";
import * as m from "../lib/model";
import { Card, MoneyInput } from "./shared";
import { Copy, ExternalLink, RefreshCw, Trash2, Layers } from "lucide-react";
import { toast } from "sonner";
import { TimelineEditor, DEFAULT_TIMELINE } from "./room/Timeline";
import { DocsManager } from "./room/Documents";
import { ClassEditor } from "./room/ClassEditor";
import { ThesisEditor, TeamEditor, PipelineEditor } from "./room/NarrativeEditors";
import { RoomInvites } from "./room/RoomInvites";
import { minOfClasses } from "../lib/classes";

export const ROOM_SECTIONS = [
  { id: "overview", label: "Opportunity", group: "The opportunity", desc: "Executive summary: headline, metric strip, why now, at-a-glance" },
  { id: "thesis", label: "Thesis", group: "The opportunity", desc: "Investment thesis, value-creation levers, exit paths" },
  { id: "market", label: "Market", group: "The opportunity", desc: "TAM funnel, cited comps, consolidators" },
  { id: "returns", label: "Returns", group: "The numbers", desc: "Distribution schedule, QSBS, pro forma, waterfall, investor calculator + sensitivity toggle" },
  { id: "sourcesuses", label: "Sources & Uses", group: "The numbers", desc: "Sources & Uses, transaction-cost detail, capitalisation" },
  { id: "financials", label: "Profit & Loss", group: "The numbers", desc: "Historical, pro-forma & projected P&L, revenue mix, Year-1 cash flow" },
  { id: "assumptions", label: "Structure & terms", group: "The numbers", desc: "Vehicle terms, deal structure, use of proceeds, key dates" },
  { id: "pipeline", label: "Pipeline / targets", group: "The plan", desc: "Add-on pipeline with expandable rows (anonymised by default)" },
  { id: "team", label: "Team", group: "The plan", desc: "Sponsor bio and the people operating the platform" },
  { id: "rollup", label: "Roll-up economics", group: "The plan", desc: "Roll-up economics illustration" },
  { id: "risks", label: "Strengths & risks", group: "Decide", desc: "What to underwrite" },
  { id: "documents", label: "Data room", group: "Decide", desc: "PPM, financials, tax returns, client files — downloadable" },
  { id: "qa", label: "Q&A", group: "Decide", desc: "Investors ask questions; you answer from the Deal Room" },
  { id: "invest", label: "Invest", group: "Decide", desc: "The Ask, soft-commit, investor calculator, timeline" },
  { id: "pass", label: "Pass", group: "Decide", desc: "Investors can decline and tell you why — feeds the Capital CRM" },
];

export const ROOM_GROUPS = ["The opportunity", "The numbers", "The plan", "Decide"];

const slug = (s) => (s || "deal").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
export const dealKeyFor = (dealId, M) => M.dealKey || dealId || `name-${slug(M.dealName)}`;
export const dealKeyAlts = (dealId, M) => [dealId, `name-${slug(M.dealName)}`].filter(Boolean);

export function useRoomShare() {
  const { M, dealId } = useDeal();
  const dealKey = dealKeyFor(dealId, M);
  const [share, setShare] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const reload = () => shareApi.byDeal(dealKey, dealKeyAlts(dealId, M)).then((s) => { setShare(s); setLoaded(true); return s; }).catch(() => setLoaded(true));
  useEffect(() => { setLoaded(false); reload(); }, [dealKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return { share, setShare, reload, loaded, dealKey };
}

export function RoomPicker({ share, onSwitched }) {
  const { M, setField, platforms } = useDeal();
  const [rooms, setRooms] = useState([]);
  useEffect(() => { shareApi.list().then(setRooms).catch(() => {}); }, [share?.id]);
  const others = rooms.filter((r) => r.dealKey !== (share?.dealKey || M.dealKey));
  if (!others.length) return null;
  const parent = M.platformRole === "addon" && M.platformId ? platforms.find((p) => p.id === M.platformId) : null;
  const fmtRoom = (r) => `${r.name || r.dealKey} — ${m.fmt$(r.commits?.total || 0)} committed · ${r.commits?.count || 0} commits · ${r.questions || 0} Q&A · ${r.views || 0} opens`;
  const pick = (val) => {
    if (val === "__new") { setField("dealKey", `dk-${Math.random().toString(36).slice(2, 10)}`); toast.success("Fresh room — raise activity and Q&A start from zero for this deal"); }
    else { const r = rooms.find((x) => x.dealKey === val); setField("dealKey", val); toast.success(`Continuing the raise in “${r?.name}” — its commitments, Q&A and activity carry over`); }
    onSwitched?.();
  };
  return (
    <Card title="Which fundraising room does this deal use?" testid="room-picker">
      <div className="ra-sub" style={{ marginBottom: 8 }}>
        New deals start with a fresh room (raise progress, Q&A and activity reset).{parent ? ` This deal is an add-on to ${parent.name} — if you are still raising for that platform, continue its existing room so commitments carry over.` : " If this deal continues an existing raise (e.g. the same platform), pick that room instead."}
      </div>
      <select className="inp" value={share ? share.dealKey : "__new"} onChange={(e) => pick(e.target.value)} data-testid="room-picker-select">
        <option value="__new">{share ? `Current room: ${share.name} (this deal)` : "New room for this deal (fresh raise, Q&A, activity)"}</option>
        {others.map((r) => <option key={r.id} value={r.dealKey}>Continue: {fmtRoom(r)}</option>)}
      </select>
    </Card>
  );
}

export function RoomSetup({ share, setShare, onPublished }) {
  const { M, scope, scopeOpts, market, dealId, runMarket, marketLoading, platforms } = useDeal();
  const parentPlatform = M.platformRole === "addon" && M.platformId ? platforms.find((p) => p.id === M.platformId) || null : null;
  const ownPlatformId = M.platformId || (M.recap && M.recap.platformId) || null;
  const ownPlatform = ownPlatformId ? platforms.find((p) => p.id === ownPlatformId) || null : null;
  const dealKey = dealKeyFor(dealId, M);
  const hasRollup = m.hasRollup(M);
  // Modeled LP ask must match what the room (and Dashboard) actually show — use the same scoped
  // model the room derives, not raw platform-only equity.
  const scopedForAsk = m.scopedModel(M, m.defaultScope(M));
  const lpEquity = m.lpAskOf(scopedForAsk) || m.lpWaterfall(scopedForAsk).lpEquity || 0;
  const [sec, setSec] = useState({ overview: true, thesis: true, market: true, financials: true, returns: true, assumptions: true, pipeline: false, team: true, rollup: hasRollup, risks: true, documents: true, qa: true, invest: true, pass: true });
  const [thesis, setThesis] = useState({});
  const [team, setTeam] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [firm, setFirm] = useState(null);
  const [blind, setBlind] = useState(false);
  const [name, setName] = useState(M.dealName || "");
  const [region, setRegion] = useState("");
  const [equityAsk, setEquityAsk] = useState(0);
  const [minCommit, setMinCommit] = useState(25000);
  const [classes, setClasses] = useState([]);
  const classesOn = classes.length > 0;
  const effMin = classesOn ? minOfClasses(classes) : +minCommit || 0;
  const [timeline, setTimeline] = useState(DEFAULT_TIMELINE);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [roomMode, setRoomMode] = useState("auto");
  // Which model view investors see — must match what the deal team is looking at, or the room returns diverge.
  const Meff = roomMode === "recap" ? { ...M, txnType: "recap" } : roomMode === "standalone" ? { ...M, txnType: "acquisition" } : M;
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (share?.notifyEmail) return;
    firmApi.get().then((f) => { setFirm(f); if (f?.email) setNotifyEmail((n) => n || f.email); }).catch(() => {});
  }, [share?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!share) return;
    if (share.notifyEmail) setNotifyEmail(share.notifyEmail);
    setBlind(!!share.blind);
    setName(share.name || M.dealName || "");
    setRegion(share.region || "");
    setEquityAsk(share.equityAsk || 0);
    setMinCommit(share.minCommit || 25000);
    setClasses(Array.isArray(share.classes) ? share.classes : []);
    if (share.timeline?.length) setTimeline(share.timeline);
    if (share.thesis) setThesis(share.thesis);
    setRoomMode(share.roomMode || "auto");
    if (Array.isArray(share.team)) setTeam(share.team);
    if (Array.isArray(share.pipeline)) setPipeline(share.pipeline);
    if (share.sections?.length) {
      const saved = new Set(share.sections);
      if (!share.sectionsV2) { saved.add("invest"); saved.add("documents"); }
      if (!share.sectionsV3) saved.add("qa");
      if (saved.has("financials") && !saved.has("sourcesuses")) saved.add("sourcesuses");
      setSec(Object.fromEntries(ROOM_SECTIONS.map((x) => [x.id, saved.has(x.id)])));
    }
  }, [share?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const f = M.deckFindings;
    if (!f || Object.keys(share?.thesis || {}).length) return;
    setThesis((t) => {
      if (t.summary?.length || t.levers?.length) return t;
      const next = { ...t };
      if (f.thesis?.length) next.summary = f.thesis;
      if (f.strengths?.length) next.levers = f.strengths.slice(0, 5);
      if (f.marketNotes?.length) next.marketBlock = { ...(t.marketBlock || {}), bullets: f.marketNotes.slice(0, 4) };
      return next;
    });
    setPipeline((p) => (p.length || !f.addOns?.length ? p : f.addOns.slice(0, 12).map((a, i) => ({
      id: `deck-${i}`, name: a.name || "", industry: "", location: a.location || "", revenue: a.revenue || 0,
      ebitda: +a.ebitda || 0, multiple: 0, stage: "Sourcing", note: a.note || "", employees: "", reveal: false, include: true,
    }))));
  }, [M.deckFindings, share?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const sections = ROOM_SECTIONS.filter((s) => sec[s.id]).map((s) => s.id);
  const roomUrl = share ? (share.roomUrl || `${window.location.origin}/room/${share.id}`) : "";

  const publish = async (regen = false) => {
    if (!sections.length) return toast.error("Pick at least one section");
    if (!(effMin > 0)) return toast.error("Set a minimum commitment above $0");
    if (classesOn && classes.some((c) => !c.name.trim() || !(+c.min > 0))) return toast.error("Every investment class needs a name and a minimum");
    setBusy(true);
    try {
      const s = await shareApi.create({ dealKey: share?.dealKey || dealKey, dealId: dealId || "", name: name || M.dealName, blind, sections, snapshot: { M, scope, market, platform: parentPlatform, ownPlatform, firm }, regenPasscode: regen, equityAsk: +equityAsk || 0, minCommit: effMin, classes, timeline, notifyEmail, thesis, team, pipeline, roomMode, region });
      setShare(s);
      onPublished?.(s);
      toast.success(share ? "Deal room updated with the latest model" : "Deal room published");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not publish the room"); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!share) return;
    await shareApi.remove(share.id);
    setShare(null);
    onPublished?.(null);
    toast.success("Link revoked");
  };
  const copy = (t, label) => { navigator.clipboard?.writeText(t); toast.success(`${label} copied`); };

  return (
    <>
    <div className="dr-grid">
      <div className="dr-col">
        <RoomPicker share={share} />
        <Card title="1 · Tabs investors will see" testid="dr-sections">
          <div className="share-secs">
            {ROOM_GROUPS.map((g) => (
              <div key={g}>
                <div className="section-label" style={{ marginTop: 6 }}>{g}</div>
                {ROOM_SECTIONS.filter((x) => x.group === g).map((s) => {
              const disabled = s.id === "rollup" && !hasRollup;
              return (
                <label key={s.id} className={`cim-sec-row ${disabled ? "dis" : ""}`} data-testid={`share-sec-${s.id}`}>
                  <input type="checkbox" checked={!!sec[s.id] && !disabled} disabled={disabled}
                    onChange={() => setSec((p) => ({ ...p, [s.id]: !p[s.id] }))} data-testid={`share-chk-${s.id}`} />
                  <span style={{ flex: 1 }}>
                    <span style={{ fontSize: 13 }}>{s.label}</span>
                    <span style={{ display: "block", fontSize: 10.5, color: "var(--text3)" }}>{disabled ? "No roll-up targets added yet" : s.desc}</span>
                    {s.id === "market" && (
                      <span className="share-mkt" data-testid="share-market-status">
                        {market ? <span className="pos">Fresh market intel from this session will be published</span>
                          : share?.hasMarket ? <span>Using market intel from the last publish</span>
                          : <span className="neg">Not generated yet — room will show a placeholder</span>}
                        {!market && <button type="button" className="link-btn lime" disabled={marketLoading} onClick={(e) => { e.preventDefault(); runMarket(); }} data-testid="share-market-generate">{marketLoading ? "Generating… (~60s)" : "Generate now"}</button>}
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
              </div>
            ))}
          </div>
          {scopeOpts && (
            <div className="field" style={{ marginTop: 12 }}><label>Deal shown to investors</label>
              <div className="scope-echo" data-testid="share-scope-echo">
                <Layers size={12} /> <b>{scopeOpts.find((o) => o.id === scope)?.label || "This deal only"}</b>
              </div>
              <span className="om-sub">The room mirrors the <b>Deal</b> selector on Assumptions — switch it there and the room's returns, S&amp;U and waterfall follow on the next autosave. No republish needed.</span>
            </div>
          )}
          <div className="grid g2" style={{ marginTop: 12 }}>
            <div className="field"><label>Room title</label><input className="inp" value={name} onChange={(e) => setName(e.target.value)} data-testid="share-name" /></div>
            <div className="field"><label>Region shown to investors</label>
              <input className="inp" value={region} onChange={(e) => setRegion(e.target.value)}
                placeholder={M.address ? `e.g. ${String(M.address).split(",").pop().trim()}` : "e.g. DFW Metroplex"} data-testid="share-region" />
              <span className="om-sub">Used in blind mode instead of the street address — the Teaser Generator pulls this same region so paper and room always match.</span>
            </div>
          </div>
          <div className="grid g2" style={{ marginTop: 12 }}>
            <div className="field"><label>Deck mode</label>
              <select className="inp" value={roomMode} onChange={(e) => setRoomMode(e.target.value)} data-testid="share-room-mode">
                <option value="auto">{`Auto — follows the model (${m.isRecap(M) ? "Recapitalization" : "Standalone acquisition"})`}</option>
                <option value="recap">Recapitalization / platform roll-up</option>
                <option value="standalone">Standalone acquisition</option>
              </select>
              <span className="om-sub">Recap decks add Acquisition strategy, Valuation landscape and platform wording; standalone decks keep it to the single business.</span>
            </div>
          </div>
          <label className="cim-sec-row" data-testid="share-blind-row">
            <input type="checkbox" checked={blind} onChange={() => setBlind(!blind)} data-testid="share-blind" />
            <span style={{ flex: 1 }}>
              <span style={{ fontSize: 13 }}>Blind mode</span>
              <span style={{ display: "block", fontSize: 10.5, color: "var(--text3)" }}>Hide address, broker and listing identifiers; show the room title only</span>
            </span>
          </label>
        </Card>

        <Card title="2 · The Ask & Timeline" testid="dr-ask">
          <div className="grid g2">
            <div className="field"><label>Equity requested ($)</label>
              <MoneyInput value={equityAsk || ""} placeholder={String(Math.round(lpEquity))} onChange={setEquityAsk} testid="share-ask" />
              <span className="ra-sub">Blank uses modeled LP equity ({m.fmt$(lpEquity)})</span>
              {+equityAsk > 0 && lpEquity > 0 && Math.abs(+equityAsk / lpEquity - 1) > 0.25 && (
                <span className="ra-sub" style={{ color: "#d9a441", display: "block", marginTop: 4 }} data-testid="share-ask-warning">
                  Heads up: the ask is {(+equityAsk / lpEquity).toFixed(1)}× the modeled equity — the room prices LP MOIC/IRR and the investor calculator on this raise size ({m.fmt$(+equityAsk)}). Set LP equity on Assumptions if the model should raise this much.
                </span>
              )}</div>
            <div className="field"><label>Minimum commitment ($)</label>
              <MoneyInput value={classesOn ? effMin : minCommit} readOnly={classesOn} onChange={setMinCommit} testid="share-min-commit" />
              <span className="ra-sub">{classesOn ? "Set by the lowest investment class below" : "Investors cannot soft-commit below this amount"}</span></div>
          </div>
          <div className="section-label">Investment classes</div>
          <ClassEditor value={classes} onChange={setClasses} basePref={M.prefReturn} />
          <div className="section-label">Deal timeline (shown to investors)</div>
          <TimelineEditor value={timeline} onChange={setTimeline} />
          <div className="field" style={{ marginTop: 14 }}><label>Notify me by email when an investor first opens the room or soft-commits</label>
            <input className="inp" type="email" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)} placeholder="you@xyzventures.com" data-testid="share-notify-email" /></div>
        </Card>

        <Card title="3 · Narrative — headline, opportunity, levers" testid="dr-thesis">
          <ThesisEditor M={Meff} market={market} platform={parentPlatform} ownPlatform={ownPlatform} value={thesis} onChange={setThesis} />
        </Card>

        <Card title="4 · Team investors will meet" testid="dr-team">
          <TeamEditor value={team} onChange={setTeam} />
        </Card>

        <Card title="5 · Add-on pipeline" testid="dr-pipeline">
          <PipelineEditor dealKey={share?.dealKey || dealKey} value={pipeline} onChange={setPipeline} />
        </Card>

        <Card title="6 · Data room (PPM, financials, tax returns, client files)" testid="dr-docs">
          {share ? <DocsManager share={share} onChange={setShare} /> : <div className="bullet">Publish the room first, then upload documents investors can download.</div>}
        </Card>
      </div>

      <div className="dr-col dr-sticky">
        <Card title="Publish" testid="dr-publish">
          <button className="btn btn-primary" style={{ width: "100%" }} onClick={() => publish(false)} disabled={busy} data-testid="share-publish">
            <RefreshCw size={13} style={{ marginRight: 6 }} />{busy ? "Publishing…" : share ? "Update room with latest model" : "Publish deal room"}
          </button>
          <div className="ra-sub" style={{ marginTop: 6 }}>Publishing pushes the current model, sections, ask and timeline. The link and passcode never change unless you regenerate or revoke.</div>
          {share && (
            <div className="share-result" data-testid="share-result">
              <div className="sr-row">
                <span className="sr-lbl">Link</span>
                <span className="sr-val mono" data-testid="share-url">{roomUrl}</span>
                <button className="icon-btn" title="Copy link" onClick={() => copy(roomUrl, "Link")} data-testid="share-copy-url"><Copy size={13} /></button>
                <a className="icon-btn" href={roomUrl} target="_blank" rel="noreferrer" title="Open" data-testid="share-open"><ExternalLink size={13} /></a>
              </div>
              <div className="ra-sub" style={{ marginTop: 2 }}>
                Investors open this link and enter the passcode with their name and email. You can also issue a
                personal link per investor below, which skips the passcode and tags everything they do in the room.
              </div>
              <div className="sr-row">
                <span className="sr-lbl">Passcode</span>
                <span className="sr-val mono sr-pass" data-testid="share-passcode">{share.passcode}</span>
                <button className="icon-btn" title="Copy passcode" onClick={() => copy(share.passcode, "Passcode")} data-testid="share-copy-pass"><Copy size={13} /></button>
                <button className="icon-btn" title="New passcode" onClick={() => publish(true)} data-testid="share-regen"><RefreshCw size={13} /></button>
              </div>
              <div className="sr-meta">
                {share.views || 0} open{share.views === 1 ? "" : "s"} · updated {share.updatedAt ? new Date(share.updatedAt).toLocaleString() : "—"}
                <button className="link-btn" onClick={revoke} data-testid="share-revoke"><Trash2 size={11} /> Revoke link</button>
              </div>
              {share.isPreview && share.brandedUrl && (
                <div className="sr-envwarn" data-testid="share-env-warning">
                  <b>You are publishing from the preview environment.</b>
                  <span>This room — and the numbers you just pushed — live only in preview. Your live site has its own database, so
                    {" "}<span className="mono">{share.brandedUrl.replace(/^https?:\/\//, "").split("/")[0]}</span> still serves whatever was last
                    published there. To update the room investors see, open the app on your own domain and press <b>Update room with latest model</b> there.</span>
                  <span className="mono sr-envurl">{share.brandedUrl}</span>
                </div>
              )}
            </div>
          )}
        </Card>

        {share && (
          <Card title="Investor access links" testid="dr-invites">
            <RoomInvites shareId={share.id} />
          </Card>
        )}
      </div>
    </div>
    </>
  );
}
